import React, { useCallback } from "react";

import StreamingChat from "../../townson-dashboard/StreamingChat";
import ScoreInsightsMessageContent from "../total-market-score/ScoreInsightsMessageContent";

/**
 * ScoreInsightsAI Component
 *
 * Wraps StreamingChat with Score Insights-specific functionality:
 * - Renders message content using ScoreInsightsMessageContent (shared renderer)
 *
 * @param {Object} props
 * @param {Object} props.dashboardData - Dashboard data to pass to AI
 * @param {string} props.conversationKey - Memory key used to keep this chat scoped to one dashboard
 * @param {boolean} props.hideInput - Whether to hide the input field
 * @param {Function} props.onMessagesChange - Callback for chat function control
 * @param {Function} props.onAddLocation - Handler for individual "Add to Map" clicks
 * @param {Function} props.onAddAllLocations - Handler for "Add All to Map" clicks
 * @param {Function} props.onLocationsExtracted - Callback when new locations are extracted from AI response (currently unused)
 * @param {Array} props.suggestedLocations - Complete location objects from backend with all scores
 * @param {Function} props.renderLocationButton - Optional custom renderer for location buttons
 * @param {Function} props.renderAddAllButton - Optional custom renderer for add all button
 * @param {string} props.initialMessage - Overrides the default intro message. Pass empty string to suppress it.
 * @param {React.ReactNode} props.initialMessageAction - Optional element rendered below the intro message (e.g. a button)
 * @param {boolean} props.persistInitialMessage - Whether to keep the intro message and action at the top.
 */
const ScoreInsightsAI = ({
	dashboardData,
	conversationKey = "score_insights",
	hideInput = false,
	onMessagesChange,
	onAddLocation,
	onAddAllLocations,
	onLocationsExtracted, // currently unused
	suggestedLocations = [],
	renderLocationButton,
	renderAddAllButton,
	initialMessage,
	initialMessageAction = null,
	persistInitialMessage = true,
}) => {
	/**
	 * Shared message renderer for StreamingChat
	 */

	const renderScoreInsightsMessage = useCallback(
		(content, messageIndex, isStreaming) => {
			return (
				<ScoreInsightsMessageContent
					content={content}
					messageIndex={messageIndex}
					suggestedLocations={suggestedLocations}
					onAddLocation={onAddLocation}
					onAddAllLocations={onAddAllLocations}
					renderLocationButton={renderLocationButton}
					renderAddAllButton={renderAddAllButton}
				/>
			);
		},
		[suggestedLocations, onAddLocation, onAddAllLocations, renderLocationButton, renderAddAllButton],
	);

	return (
		<StreamingChat
			dashboardType='score_insights'
			conversationKey={conversationKey}
			dashboardData={dashboardData}
			hideInput={hideInput}
			onMessagesChange={onMessagesChange}
			renderMessageContent={renderScoreInsightsMessage}
			apiEndpoint='/api/v3/dashboard/stream-analyze'
			initialMessage={
				initialMessage ??
				`
			<div>
				<p>
					Bluechip AI can recommend up to 3 Locations based on your current dashboard settings. These recommendations can score higher than the Locations you manually input and may be a good area to further investigate. Click the button below to begin.
				</p>
				<p class="text-center"><small><strong>AI recommendations can only be used once per Score Insight dashboard.</strong></small></p>
			</div>
		`
			}
			initialMessageIsHtml={true}
			persistInitialMessage={persistInitialMessage}
			initialMessageAction={initialMessageAction}
		/>
	);
};

export default ScoreInsightsAI;
